
import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Check, ChevronsUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { FarmPrice, CreateFarmPriceInput } from '@/types/database/farm-prices';
import { useGetProducts } from '@/hooks/use-products';
import { useFarmRegions } from '@/hooks/use-farm-regions';

const farmPriceSchema = z.object({
  product_name: z.string().min(1, 'Product name is required'),
  region: z.string().min(1, 'Region is required'),
  origin: z.string().optional(),
  source_type: z.string().optional(),
  supplier_name: z.string().optional(),
  farm_price_per_kg: z.number().min(0, 'Farm price must be non-negative'),
  sack_crate_cost_per_kg: z.number().min(0).optional(),
  loading_cost_per_kg: z.number().min(0).optional(),
  transportation_cost_per_kg: z.number().min(0).optional(),
  border_cost_per_kg: z.number().min(0).optional(),
  agent_cost_per_kg: z.number().min(0).optional(),
  miscellaneous_costs: z.number().min(0).optional(),
  vat: z.number().min(0).optional(),
  price: z.number().min(0),
  product_remark: z.string().optional(),
  date: z.string().min(1, 'Date is required'),
});

interface FarmPriceFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateFarmPriceInput) => void;
  editingPrice?: FarmPrice;
}

const FarmPriceForm: React.FC<FarmPriceFormProps> = ({
  open,
  onClose,
  onSubmit,
  editingPrice,
}) => {
  const { data: products = [], isLoading: isLoadingProducts } = useGetProducts();
  const { data: regions = [], isLoading: isLoadingRegions } = useFarmRegions();
  const [productOpen, setProductOpen] = React.useState(false);
  const [regionOpen, setRegionOpen] = React.useState(false);

  const form = useForm<CreateFarmPriceInput>({
    resolver: zodResolver(farmPriceSchema),
    defaultValues: editingPrice ? {
      product_name: editingPrice.product_name,
      region: editingPrice.region,
      origin: editingPrice.origin || '',
      source_type: editingPrice.source_type || '',
      supplier_name: editingPrice.supplier_name || '',
      farm_price_per_kg: editingPrice.farm_price_per_kg,
      sack_crate_cost_per_kg: editingPrice.sack_crate_cost_per_kg || 0,
      loading_cost_per_kg: editingPrice.loading_cost_per_kg || 0,
      transportation_cost_per_kg: editingPrice.transportation_cost_per_kg || 0,
      border_cost_per_kg: editingPrice.border_cost_per_kg || 0,
      agent_cost_per_kg: editingPrice.agent_cost_per_kg || 0,
      miscellaneous_costs: editingPrice.miscellaneous_costs || 0,
      vat: editingPrice.vat || 0,
      price: editingPrice.price,
      product_remark: editingPrice.product_remark || '',
      date: editingPrice.date,
    } : {
      product_name: '',
      region: '',
      origin: '',
      source_type: '',
      supplier_name: '',
      farm_price_per_kg: 0,
      sack_crate_cost_per_kg: 0,
      loading_cost_per_kg: 0,
      transportation_cost_per_kg: 0,
      border_cost_per_kg: 0,
      agent_cost_per_kg: 0,
      miscellaneous_costs: 0,
      vat: 0,
      price: 0,
      product_remark: '',
      date: new Date().toISOString().split('T')[0],
    },
  });

  const watchedValues = form.watch();
  
  // Calculate total price whenever cost values change
  const calculateTotalPrice = () => {
    return (
      (watchedValues.farm_price_per_kg || 0) +
      (watchedValues.sack_crate_cost_per_kg || 0) +
      (watchedValues.loading_cost_per_kg || 0) +
      (watchedValues.transportation_cost_per_kg || 0) +
      (watchedValues.border_cost_per_kg || 0) +
      (watchedValues.agent_cost_per_kg || 0) +
      (watchedValues.miscellaneous_costs || 0)
    );
  };

  const totalPrice = calculateTotalPrice();

  // Update the price field whenever costs change
  React.useEffect(() => {
    form.setValue('price', totalPrice);
  }, [totalPrice, form]);

  const handleSubmit = (data: CreateFarmPriceInput) => {
    // Ensure price is calculated correctly before submission
    data.price = calculateTotalPrice();
    onSubmit(data);
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editingPrice ? 'Edit Farm Price' : 'Add New Farm Price'}
          </DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="product_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Product Name *</FormLabel>
                    <FormControl>
                      <Popover open={productOpen} onOpenChange={setProductOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={productOpen}
                            className="w-full justify-between"
                            disabled={isLoadingProducts}
                          >
                            {field.value || "Search and select product..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search products..." />
                            <CommandList className="max-h-[200px] overflow-y-auto">
                              <CommandEmpty>No product found.</CommandEmpty>
                              <CommandGroup>
                                {products.map((product) => (
                                  <CommandItem
                                    key={product}
                                    value={product}
                                    onSelect={(currentValue) => {
                                      field.onChange(currentValue === field.value ? "" : currentValue);
                                      setProductOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        field.value === product ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    {product}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="region"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Region *</FormLabel>
                    <FormControl>
                      <Popover open={regionOpen} onOpenChange={setRegionOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant="outline"
                            role="combobox"
                            aria-expanded={regionOpen}
                            className="w-full justify-between"
                            disabled={isLoadingRegions}
                          >
                            {field.value || "Search and select region..."}
                            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-full p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Search regions..." />
                            <CommandList className="max-h-[200px] overflow-y-auto">
                              <CommandEmpty>No region found.</CommandEmpty>
                              <CommandGroup>
                                {regions.map((region) => (
                                  <CommandItem
                                    key={region}
                                    value={region}
                                    onSelect={(currentValue) => {
                                      field.onChange(currentValue === field.value ? "" : currentValue);
                                      setRegionOpen(false);
                                    }}
                                  >
                                    <Check
                                      className={cn(
                                        "mr-2 h-4 w-4",
                                        field.value === region ? "opacity-100" : "opacity-0"
                                      )}
                                    />
                                    {region}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="origin"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Origin</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="source_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Source Type</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="supplier_name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Supplier Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Date *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="border-t pt-4">
              <h3 className="text-lg font-semibold mb-4">Cost Breakdown (ETB per Kg)</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <FormField
                  control={form.control}
                  name="farm_price_per_kg"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Farm Price per Kg *</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="sack_crate_cost_per_kg"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Sack/Crate Cost per Kg</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="loading_cost_per_kg"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Loading Cost per Kg</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="transportation_cost_per_kg"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Transportation Cost per Kg</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="border_cost_per_kg"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Border Cost per Kg</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="agent_cost_per_kg"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Agent Cost per Kg</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="miscellaneous_costs"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Miscellaneous Costs</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="vat"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>VAT</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.01"
                          {...field}
                          onChange={(e) => field.onChange(parseFloat(e.target.value) || 0)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                <div className="text-lg font-semibold">
                  Total Price per Kg: ETB {totalPrice.toFixed(2)}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  (Excluding VAT - VAT is tracked separately)
                </div>
              </div>
            </div>

            <FormField
              control={form.control}
              name="product_remark"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Product Remark</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={3} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit">
                {editingPrice ? 'Update Price' : 'Add Price'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default FarmPriceForm;
